import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Coins, Save, Wrench, Banknote, Gift } from "lucide-react";

const COST_CARDS = [
  { key: "COSTO_IA_TEXTO", label: "Modificación sencilla", icon: Wrench, color: "#34D399", hint: "Comandos de texto con IA (corregir, traducir, colores)" },
  { key: "COSTO_EDITOR", label: "Modificación media", icon: Save, color: "#C084FC", hint: "Guardar y re-exportar en el editor" },
  { key: "COSTO_IA_IMAGEN", label: "Modificación compleja", icon: Wrench, color: "#F472B6", hint: "Edición de imagen con IA (quitar fondo, cambiar textos, reencuadrar)" },
  { key: "COSTO_GEN_1K", label: "Generación de imagen 1K", icon: Coins, color: "#FF4D4D", hint: "Generar fondo o diseño 3D en 1K" },
  { key: "COSTO_GEN_2K", label: "Generación/edición 2K", icon: Coins, color: "#E11D2E", hint: "Generar fondo o diseño 3D en 2K" },
  { key: "COSTO_GEN_4K", label: "Generación/edición 4K", icon: Coins, color: "#6366F1", hint: "Generar fondo o diseño 3D en 4K" },
  { key: "COSTO_OPERACION", label: "PDF/imagen sencillo", icon: Wrench, color: "#F59E0B", hint: "Reconstruir en capas 1 página" },
  { key: "COSTO_PDF_MEDIO", label: "PDF mediano (2-5 págs.)", icon: Wrench, color: "#FB923C", hint: "Reconstruir en capas de 2 a 5 páginas" },
  { key: "COSTO_PDF_PESADO", label: "PDF pesado (6+ págs.)", icon: Wrench, color: "#F87171", hint: "Reconstruir en capas 6 páginas o más" },
  { key: "MIN_PURCHASE_CREDITS", label: "Compra mínima", icon: Coins, color: "#94A3B8", hint: "Mínimo de créditos por compra" },
  { key: "CREDITOS_BIENVENIDA", label: "Regalo de bienvenida", icon: Gift, color: "#22D3EE", hint: "Créditos gratis para cada miembro nuevo (0 = sin regalo)" },
  { key: "CREDITOS_REFERIDO", label: "Premio por invitar", icon: Gift, color: "#A3E635", hint: "Créditos para cada uno cuando el amigo hace su primera edición" },
  { key: "MAX_REFERIDOS_PREMIADOS", label: "Tope de invitaciones", icon: Gift, color: "#60A5FA", hint: "Cuántos amigos premiados máximo por persona" },
];

const CREDIT_PRICE_MXN = 3;

export default function Costs() {
  const { data: settings, refetch } = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await api.get("/admin/settings")).data,
  });
  const [values, setValues] = useState({});
  const [paymentInfo, setPaymentInfo] = useState("");

  useEffect(() => { if (settings) setValues(settings); }, [settings]);

  useEffect(() => {
    api.get("/admin/payment-info")
      .then(({ data }) => setPaymentInfo(data.text || ""))
      .catch((e) => console.warn("No se pudieron cargar los datos de pago:", e));
  }, []);

  const savePaymentInfo = async () => {
    try {
      await api.put("/admin/payment-info", { text: paymentInfo });
      toast.success("Datos de pago actualizados");
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const save = async (key) => {
    const val = parseInt(values[key], 10);
    if (isNaN(val) || val < 0) return toast.error("Valor inválido");
    try {
      await api.put("/admin/settings", { key, value: val });
      toast.success("Valor actualizado");
      refetch();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <Layout>
      <PageHeader title="Configuración de Costos" subtitle={`Créditos por operación · 1 crédito = $${CREDIT_PRICE_MXN} MXN`} />
      <div className="p-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl">
          {COST_CARDS.map((c) => (
            <div key={c.key} data-testid={`cost-card-${c.key}`} className="rounded-lg border p-6"
              style={{ backgroundColor: "#111827", borderColor: "#1E293B" }}>
              <div className="flex items-center gap-2 mb-2">
                <c.icon className="w-5 h-5" style={{ color: c.color }} />
                <span className="font-heading font-semibold text-slate-200 text-sm">{c.label}</span>
              </div>
              <p className="text-xs text-slate-500 mb-4">{c.hint}</p>
              <Input data-testid={`cost-input-${c.key}`} type="number" value={values[c.key] ?? ""}
                onChange={(e) => setValues({ ...values, [c.key]: e.target.value })}
                className="bg-[#0D111A] border-[#1E293B] text-slate-100 font-mono text-lg" />
              <div data-testid={`cost-price-${c.key}`} className="text-[11px] text-amber-400 mt-1.5 font-mono">
                ≈ ${((parseInt(values[c.key], 10) || 0) * CREDIT_PRICE_MXN).toLocaleString()} MXN
              </div>
              <Button data-testid={`cost-save-${c.key}`} onClick={() => save(c.key)}
                className="w-full mt-4 bg-[#C2620E] hover:bg-[#FF8A2B] text-white">Guardar</Button>
            </div>
          ))}
        </div>

        <div className="rounded-lg border p-6 max-w-4xl mt-6"
          style={{ backgroundColor: "#111827", borderColor: "#1E293B" }}
          data-testid="payment-info-card">
          <div className="flex items-center gap-2 mb-2">
            <Banknote className="w-5 h-5" style={{ color: "#34D399" }} />
            <span className="font-heading font-semibold text-slate-200 text-sm">
            Datos de pago (transferencia u OXXO) · solo para tratos del mercado
          </span>
        </div>
          <p className="text-xs text-slate-500 mb-4">
            Los créditos ya se cobran con tarjeta (Stripe). Esto solo aparece si alguien pide
            créditos por el camino manual antiguo o para cerrar tratos del mercado: pon tu banco,
            CLABE, titular o número OXXO y cómo mandarte el comprobante.
          </p>
          <textarea data-testid="payment-info-input" rows={6} value={paymentInfo}
            onChange={(e) => setPaymentInfo(e.target.value)}
            placeholder={"Banco: ...\nCLABE: ...\nTitular: ...\nOXXO: ...\nManda tu comprobante por Telegram a @..."}
            className="w-full rounded-md bg-[#0D111A] border border-[#1E293B] text-slate-100 text-sm p-3 outline-none resize-y" />
          <Button data-testid="payment-info-save" onClick={savePaymentInfo}
            className="mt-4 bg-[#C2620E] hover:bg-[#FF8A2B] text-white">Guardar datos de pago</Button>
        </div>
      </div>
    </Layout>
  );
}
